import { useEffect, useMemo, useRef, useState } from "react";
import get from "lodash.get";
import { isEmpty, isLoaded } from "react-redux-firebase";
import IGlobalConfig from "../types/IGlobalConfig";
import {
  fetchGlobalConfigFromRest,
  getRtdbConfigWaitMs,
} from "./globalConfigRest";
import { applyLocalServiceUrls } from "./localServiceUrls";

export const CONFIG_LOAD_ERROR_MESSAGE =
  "We're having some troubles reaching out ViewSpark. Please try again later.";

const RTDB_POLL_MS = 250;

export interface FirebaseConfigGateSlice {
  auth?: unknown;
  data?: {
    global?: {
      config?: IGlobalConfig | null;
    };
  };
}

function hasAuthServiceUrl(config: IGlobalConfig | null | undefined): boolean {
  return Boolean(config?.authServiceUrl);
}

function rtdbGlobalConfigSnapshot(
  firebase?: FirebaseConfigGateSlice | null
): IGlobalConfig | null | undefined {
  return get(firebase, "data.global.config") as IGlobalConfig | null | undefined;
}

function isRtdbAuthReady(firebase?: FirebaseConfigGateSlice | null): boolean {
  if (!firebase) {
    return false;
  }
  return isLoaded(firebase.auth);
}

function isRtdbGlobalConfigResolved(firebase?: FirebaseConfigGateSlice | null): boolean {
  if (!firebase) {
    return false;
  }
  return isLoaded(rtdbGlobalConfigSnapshot(firebase));
}

export type RtdbConfigWaitOutcome = "wait" | "rest" | "done";

export function resolveRtdbConfigWaitOutcome(
  firebase: FirebaseConfigGateSlice | null | undefined,
  reduxConfig: IGlobalConfig | null | undefined,
  startedAtMs: number,
  nowMs = Date.now()
): RtdbConfigWaitOutcome {
  const rtdbConfig = rtdbGlobalConfigSnapshot(firebase);
  if (hasAuthServiceUrl(reduxConfig) || hasAuthServiceUrl(rtdbConfig)) {
    return "done";
  }
  const timedOut = nowMs - startedAtMs >= getRtdbConfigWaitMs();
  if (!firebase || !isRtdbAuthReady(firebase)) {
    return timedOut ? "rest" : "wait";
  }
  if (!isRtdbGlobalConfigResolved(firebase)) {
    return timedOut ? "rest" : "wait";
  }
  if (isEmpty(rtdbConfig) || !hasAuthServiceUrl(rtdbConfig)) {
    return "rest";
  }
  return "done";
}

export function useEffectiveGlobalConfig(
  reduxConfig?: IGlobalConfig | null,
  firebase?: FirebaseConfigGateSlice | null
) {
  const [fallbackConfig, setFallbackConfig] = useState<IGlobalConfig | null>(null);
  const [configLoadError, setConfigLoadError] = useState(false);
  const reduxConfigRef = useRef(reduxConfig);
  const firebaseRef = useRef(firebase);

  reduxConfigRef.current = reduxConfig;
  firebaseRef.current = firebase;

  const rtdbConfig = rtdbGlobalConfigSnapshot(firebase);

  const loadedConfig = useMemo(() => {
    if (hasAuthServiceUrl(reduxConfig)) {
      return reduxConfig as IGlobalConfig;
    }
    if (hasAuthServiceUrl(rtdbConfig)) {
      return rtdbConfig as IGlobalConfig;
    }
    if (hasAuthServiceUrl(fallbackConfig)) {
      return fallbackConfig as IGlobalConfig;
    }
    return reduxConfig ?? rtdbConfig ?? fallbackConfig ?? undefined;
  }, [reduxConfig, rtdbConfig, fallbackConfig]);
  const effectiveConfig = applyLocalServiceUrls(loadedConfig);

  const isConfigReady = hasAuthServiceUrl(effectiveConfig);

  useEffect(() => {
    if (hasAuthServiceUrl(reduxConfig) || hasAuthServiceUrl(rtdbGlobalConfigSnapshot(firebase))) {
      setFallbackConfig(null);
      setConfigLoadError(false);
      return;
    }

    let cancelled = false;
    let restAttempted = false;
    const startedAt = Date.now();

    setConfigLoadError(false);

    const tryRestFallback = () => {
      if (restAttempted || cancelled) {
        return;
      }
      restAttempted = true;
      fetchGlobalConfigFromRest()
        .then(config => {
          if (cancelled) {
            return;
          }
          if (hasAuthServiceUrl(config)) {
            setFallbackConfig(config);
            setConfigLoadError(false);
            return;
          }
          setConfigLoadError(true);
        })
        .catch(() => {
          if (!cancelled) {
            setConfigLoadError(true);
          }
        });
    };

    const tick = (): boolean => {
      if (cancelled) {
        return true;
      }
      const outcome = resolveRtdbConfigWaitOutcome(
        firebaseRef.current,
        reduxConfigRef.current,
        startedAt
      );
      if (outcome === "done") {
        return true;
      }
      if (outcome === "rest") {
        tryRestFallback();
        return true;
      }
      return false;
    };

    const pollId = window.setInterval(() => {
      if (tick()) {
        window.clearInterval(pollId);
      }
    }, RTDB_POLL_MS);
    tick();

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, [
    reduxConfig?.authServiceUrl,
    rtdbConfig?.authServiceUrl,
    firebase?.auth,
    firebase?.data?.global?.config,
  ]);

  return {
    effectiveConfig,
    isConfigReady,
    configLoadError: configLoadError && !isConfigReady,
  };
}

if (__DEV__) {
  const t0 = 1000;
  const t8 = t0 + getRtdbConfigWaitMs();
  if (!(resolveRtdbConfigWaitOutcome(null, null, t0, t0 + 100) === "wait")) {
    console.error("useEffectiveGlobalConfig: wait before RTDB timeout");
  }
  if (!(resolveRtdbConfigWaitOutcome(null, null, t0, t8) === "rest")) {
    console.error("useEffectiveGlobalConfig: REST after RTDB timeout");
  }
  if (
    !(
      resolveRtdbConfigWaitOutcome(
        null,
        { authServiceUrl: "https://auth.example.com" },
        t0,
        t0 + 100
      ) === "done"
    )
  ) {
    console.error("useEffectiveGlobalConfig: done when redux config ready");
  }
}
